import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { arrayUnion, collection, deleteDoc, doc, Firestore, getDoc, getDocs, increment, onSnapshot, serverTimestamp, setDoc, updateDoc } from '@angular/fire/firestore';
import { PeerConfig, PresenceState, UserPresenceDoc } from '../../interfaces/master-interfaces';
import { DeviceService } from '../admin services/device.service';


const LOG_PREFIX = '[PresenceService]';

/** Single source of truth for the presence collection name. */
const USERS_COLLECTION = 'anonymous_users';

/**
 * Two staleness windows (Firestore has no onDisconnect, so lastSeen freshness
 * is the only reliable liveness signal):
 *   ACTIVE  — foreground heartbeat is ~25s; older than this while
 *             isTabActive=true means a frozen/crashed foreground tab.
 *   SESSION — how long we tolerate silence from a backgrounded session before
 *             treating the user as gone.
 */


/** Foreground heartbeat cadence. */
const HEARTBEAT_MS = 25_000;


@Injectable({
  providedIn: 'root'
})



export class PresenceService {


  roomId: string = '';
  private userId?: string;
  private currentRoomId?: string;
  private lastSuccessfulConfig?: PeerConfig;

  // ---- App-level session lifecycle (route-agnostic) ----
  private sessionUserId?: string;
  private sessionActive = false;
  private heartbeatInterval?: ReturnType<typeof setInterval>;
  private visibilityHandler?: () => void;
  private onlineHandler?: () => void;
  private offlineHandler?: () => void;
  private pageHideHandler?: () => void;
  private visibilityCleanup?: () => void;

  constructor(
    private _firestore: Firestore,
    private _ngZone: NgZone
  ) { }

  public setCurrentUser(userId: string): void {
    this.userId = userId;
  }

  // =========================================================================
  // ✅ APP-LEVEL PRESENCE SESSION
  //
  // Call startSession() ONCE from the root scope (AppComponent) when the
  // user's identity is known. It lives for the whole app and is unaffected
  // by route changes, so isUserPresent stays true as the user moves between
  // pages. It only flips to false on a real app exit (pagehide) or an
  // explicit endSession().
  // =========================================================================

  async startSession(userId: string): Promise<void> {
    if (!userId) {
      console.warn(`${LOG_PREFIX} startSession called without userId`);
      return;
    }

    // Idempotent: same user, already running → no-op.
    if (this.sessionActive && this.sessionUserId === userId) {
      //  console.log(`${LOG_PREFIX} Session already active for ${userId} - skipping re-init`);
      return;
    }

    // Switching identity → tear the old one down first.
    if (this.sessionActive && this.sessionUserId && this.sessionUserId !== userId) {
      //  console.log(`${LOG_PREFIX} Switching users: ending old session for ${this.sessionUserId}`);
      await this.endSession();
    }

    this.sessionUserId = userId;
    this.sessionActive = true;

    // Session start write
    await this.setUserPresent(userId);

    // All listeners live outside Angular to avoid change-detection churn.
    this._ngZone.runOutsideAngular(() => {
      // ===== VISIBILITY HANDLER =====
      this.visibilityHandler = () => {
        const active = !document.hidden && navigator.onLine;
        // console.log(
        //   `${LOG_PREFIX} [visibilitychange] active=${active} ` +
        //   `(hidden=${document.hidden}, online=${navigator.onLine})`
        // );
        if (active) {
          this.startHeartbeat(userId);
        } else {
          this.stopHeartbeat();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);

      // ===== NETWORK HANDLERS =====
      this.onlineHandler = () => {
        // console.log(`${LOG_PREFIX} [online] network restored`);
        // Only restart heartbeat if we're also visible
        if (!document.hidden) {
          this.startHeartbeat(userId);
        }
      };

      this.offlineHandler = () => {
        //  console.log(`${LOG_PREFIX} [offline] network lost`);
        this.stopHeartbeat();
      };

      window.addEventListener('online', this.onlineHandler);
      window.addEventListener('offline', this.offlineHandler);

      // ===== PAGE EXIT (UNLOAD) =====
      // pagehide is more reliable than beforeunload on mobile Safari
      this.pageHideHandler = () => {
        //  console.log(`${LOG_PREFIX} [pagehide] app closing - marking user not present`);
        // this.setUserNotPresent(userId).catch(() => { });
      };
      window.addEventListener('pagehide', this.pageHideHandler);

      // ===== iOS FIX: PAGESHOW (APP RESUME) =====
      // This fires when the app returns from background on iOS
      // More reliable than visibilitychange for recovery
      const pageShowHandler = () => {
        // console.log(
        //   `${LOG_PREFIX} [pageshow] app resumed - validating presence ` +
        //   `(hidden=${document.hidden}, online=${navigator.onLine})`
        // );

        // Re-validate state when app comes back
        if (!document.hidden && navigator.onLine) {
          //  console.log(`${LOG_PREFIX} [pageshow] Foreground + online → restarting heartbeat`);
          this.startHeartbeat(userId);
        } else {
          //  console.log(`${LOG_PREFIX} [pageshow] Hidden or offline → stopping heartbeat`);
          this.stopHeartbeat();
        }
      };
      window.addEventListener('pageshow', pageShowHandler);

      // ===== INITIAL STATE =====
      if (!document.hidden && navigator.onLine) {
        // console.log(`${LOG_PREFIX} Initial state: foreground + online → starting heartbeat`);
        this.startHeartbeat(userId);
      } else {
        // console.log(
        //   `${LOG_PREFIX} Initial state: ` +
        //   `${document.hidden ? 'hidden' : 'visible'}, ` +
        //   `${navigator.onLine ? 'online' : 'offline'}`
        // );
      }
    });

  }


  async endSession(): Promise<void> {
    const userId = this.sessionUserId;

    this.stopHeartbeat();

    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = undefined;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = undefined;
    }
    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
      this.offlineHandler = undefined;
    }
    if (this.pageHideHandler) {
      window.removeEventListener('pagehide', this.pageHideHandler);
      this.pageHideHandler = undefined;
    }

    this.sessionActive = false;
    this.sessionUserId = undefined;

    // console.log(`${LOG_PREFIX} session ended`);
  }



  private startHeartbeat(userId: string): void {
    this.stopHeartbeat();
    this._ngZone.runOutsideAngular(() => {
      this.heartbeatInterval = setInterval(() => {
        this.heartbeat(userId).catch(err =>
          console.error(`${LOG_PREFIX} heartbeat failed:`, err)
        );
      }, HEARTBEAT_MS);
    });
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = undefined;
    }
  }



  // =========================================================================
  // ✅ USERS COLLECTION ('anonymous_users') — decoupled presence model
  //
  //   isUserPresent  = session is open (user is IN the app, any route)
  //   isTabActive    = tab is foregrounded (and online) RIGHT NOW
  //   lastSeen       = liveness timestamp, refreshed by heartbeat while active
  // =========================================================================

  private userDocRef(userId: string) {
    return doc(this._firestore, USERS_COLLECTION, userId);
  }



  /** SESSION START — mark the user as in the app. */
  async setUserPresent(userId: string): Promise<void> {
    if (!userId) {
      console.warn('⚠️ Cannot set user present: missing userId');
      return;
    }

    try {
      const updateData: any = {
        isUserPresent: true,
        isTabActive: !document.hidden,
        lastSeen: serverTimestamp()
      };

      await setDoc(this.userDocRef(userId), updateData, { merge: true });
    } catch (error: any) {
      console.error('❌ Failed to set user present:', {
        message: error.message,
        code: error.code,
        userId
      });
    }
  }


  /** LIVENESS — refresh lastSeen only. Runs on the foreground heartbeat. */
  async heartbeat(userId: string): Promise<void> {
    if (!userId) return;
    try {
      await setDoc(this.userDocRef(userId), { lastSeen: serverTimestamp() }, { merge: true });
    } catch (error: any) {
      console.error('❌ Failed to update heartbeat:', {
        message: error.message,
        code: error.code,
        userId
      });
    }
  }


  public setupVisibilityTracking(tabActive$: BehaviorSubject<boolean>): () => void {
    const handleVisibilityChange = () => {
      tabActive$.next(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }

  public cleanupUserTracking(): void {
    if (this.visibilityCleanup) {
      this.visibilityCleanup();
      this.visibilityCleanup = undefined;
    }
  }
}